import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useColorModeValue,
  IconButton,
  Image,
  Kbd,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../contexts/FilterContext';
import { useAuth } from '../../contexts/AuthContext';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import CommandMenu from './CommandMenu';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import useReviewCount from '../../hooks/useReviewCount';
import useAccountFreshness from '../../hooks/useAccountFreshness';
import { getAccounts } from '../../api/endpointsBackend';
import { sortByRecentActivity } from '../../utils/accountActivity';
import { OPEN_ASSISTANT_EVENT } from '../chat/AssistantWidget';

import {
  LuLayoutDashboard,
  LuArrowLeftRight,
  LuLandmark,
  LuFolder,
  LuTag,
  LuTarget,
  LuFileUp,
  LuSettings,
  LuLogOut,
  LuPanelLeftClose,
  LuPanelLeftOpen,
  LuSearch,
  LuSparkles,
  LuEllipsis,
} from 'react-icons/lu';

// The shortcut modifier as the keyboard in front of the reader prints it.
const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘' : 'Ctrl ';

// How many accounts the sidebar names before it points to the Accounts page.
const SIDEBAR_ACCOUNTS = 6;

// The pages the sidebar lists, in order, for the reader's role and settings.
// The ones marked `more` sit behind the sidebar's "More" menu, as Linear keeps
// its rarer pages; the ⌘K menu still lists every page.
function useNavItems() {
  const { t } = useTranslation('navigation');
  const { can } = useWorkspace();
  const goalsEnabled = useGoalsEnabled();
  return [
    { icon: <LuLayoutDashboard />, label: t('dashboard'),    to: '/dashboard', dataTour: 'tour-dashboard' },
    { icon: <LuArrowLeftRight />,  label: t('movements'),    to: '/movements', dataTour: 'tour-movements' },
    { icon: <LuLandmark />,        label: t('accounts'),     to: '/accounts', more: true },
    { icon: <LuFolder />,          label: t('categories'),   to: '/categories', more: true },
    { icon: <LuTag />,              label: t('tags'),         to: '/tags', more: true },
    goalsEnabled && can('goal.read') && {
      icon: <LuTarget />, label: t('goals'), to: '/goals', more: true,
    },
    can('source_file.read') && {
      icon: <LuFileUp />, label: t('sourceFiles'), to: '/source-files', dataTour: 'tour-sourcefiles', more: true,
    },
  ].filter(Boolean);
}

// Navigation item component. `count` is a number beside the label (what is
// waiting there); collapsed, it becomes a dot on the icon. It forwards its ref
// and other props so it can be a menu's button.
const NavItem = React.forwardRef(({ icon, label, to, isActive, onClick, isCollapsed, dataTour, count, countLabel, countColor = 'redesign.text.muted', trailing, ...rest }, ref) => {
  return (
    <Box
      ref={ref}
      {...rest}
      as={to ? Link : 'button'}
      to={to}
      onClick={onClick}
      data-tour={dataTour}
      position="relative"
      w="100%"
      display="flex"
      alignItems="center"
      justifyContent={isCollapsed ? 'center' : 'flex-start'}
      gap={isCollapsed ? 0 : '12px'}
      px="11px"
      py="7px"
      borderRadius="8px"
      // The active item sits raised on the chrome, as Linear's does: its own
      // ground, a hairline, and in Paper a faint lift.
      bg={isActive ? 'redesign.bg.raised' : 'transparent'}
      border="1px solid"
      borderColor={isActive ? 'redesign.border' : 'transparent'}
      boxShadow={isActive ? '0 1px 2px rgba(16, 24, 40, 0.05)' : 'none'}
      _dark={{ boxShadow: 'none' }}
      color={isActive ? 'redesign.heading' : 'redesign.text.muted'}
      fontWeight="500"
      fontSize={["11px", "12px", "13px"]}
      lineHeight="1.2"
      letterSpacing="-0.005em"
      _hover={{
        bg: isActive ? 'redesign.bg.raised' : 'blackAlpha.50',
        color: 'redesign.heading',
        _dark: { bg: isActive ? 'redesign.bg.raised' : 'whiteAlpha.50' },
      }}
      transition="background-color 0.15s, color 0.15s"
      textDecoration="none"
      title={isCollapsed ? label : undefined}
    >
      <Box
        fontSize={["16px", "17px", "18px"]}
        display="flex"
        alignItems="center"
        color={isActive ? 'redesign.accent.blue' : 'inherit'}
      >
        {icon}
      </Box>
      {!isCollapsed && <Text whiteSpace="nowrap" fontSize={["11px", "12px", "13px"]} fontWeight="500" flex={1} textAlign="left">{label}</Text>}
      {!isCollapsed && trailing}
      {!isCollapsed && count > 0 && (
        <Text as="span" fontSize="sm" fontWeight={500} color={countColor} textStyle="tabularNumbers" title={countLabel}>
          {count}
        </Text>
      )}
      {isCollapsed && count > 0 && (
        <Box as="span" position="absolute" top="6px" right="10px" w="6px" h="6px" borderRadius="full" bg={countColor} title={countLabel} />
      )}
    </Box>
  );
});

// The active accounts, named and opened from the sidebar, the ones with the
// latest movements first. Balances stay on the Accounts page: the only source
// for them is that page's full load.
function SidebarAccounts({ onClose }) {
  const { t } = useTranslation('navigation');
  const [accounts, setAccounts] = useState(null);
  useEffect(() => {
    let active = true;
    getAccounts()
      .then((rows) => { if (active) setAccounts(sortByRecentActivity(rows.filter((a) => a.is_active))); })
      .catch(() => { if (active) setAccounts([]); });
    return () => { active = false; };
  }, []);
  if (!accounts || accounts.length === 0) return null;
  const shown = accounts.slice(0, SIDEBAR_ACCOUNTS);
  const more = accounts.length - shown.length;
  return (
    <Box pt="14px">
      <Text textStyle="microLabel" px="11px" pb="6px">{t('accounts')}</Text>
      {shown.map((a) => (
        <Flex
          key={a.id}
          as={Link}
          to={`/accounts?account_id=${a.id}`}
          onClick={onClose}
          align="center"
          gap="10px"
          h="28px"
          px="11px"
          borderRadius="7px"
          color="redesign.text"
          fontSize="13px"
          textDecoration="none"
          _hover={{ bg: 'blackAlpha.50', color: 'redesign.heading', _dark: { bg: 'whiteAlpha.50' } }}
        >
          <Flex w="16px" justify="center" flexShrink={0}>
            {a.source_logo_url ? (
              <Image src={a.source_logo_url} alt="" boxSize="14px" objectFit="contain" borderRadius="3px"
                fallback={<Box w="8px" h="8px" borderRadius="full" bg={a.color || 'redesign.text.faint'} />} />
            ) : (
              <Box w="8px" h="8px" borderRadius="full" bg={a.color || 'redesign.text.faint'} />
            )}
          </Flex>
          <Text flex={1} minW={0} isTruncated>{a.name}</Text>
        </Flex>
      ))}
      {more > 0 && (
        <Text as={Link} to="/accounts" onClick={onClose} display="block" px="37px" py="4px" fontSize="12px" color="redesign.text.muted" _hover={{ color: 'redesign.heading' }}>
          {t('sidebar.moreAccounts', { count: more })}
        </Text>
      )}
    </Box>
  );
}

// Logo component
const SidebarLogo = ({ isCollapsed, toggleSidebar }) => {
  const navigate = useNavigate();
  const { goToToday } = useFilters();
  const { t } = useTranslation('navigation');
  const logoSrc = useColorModeValue('/brand/wedge-k-light.svg', '/brand/wedge-k-dark.svg');

  const handleLogoClick = () => {
    goToToday();
    navigate('/movements');
  };

  return (
    <Flex
      align="center"
      justify={isCollapsed ? 'center' : 'space-between'}
      px="16px"
      pt="16px"
      pb="10px"
    >
      {!isCollapsed && (
        <Flex
          align="center"
          gap="8px"
          cursor="pointer"
          onClick={handleLogoClick}
          overflow="hidden"
        >
          <Box
            as="img"
            src={logoSrc}
            alt="Mi Kapital"
            width="24px"
            height="24px"
            flexShrink={0}
          />
          <Text
            fontSize={["13px", "14px", "15px"]}
            fontWeight="600"
            color="redesign.heading"
            letterSpacing="-0.02em"
            whiteSpace="nowrap"
          >
            Mi Kapital
          </Text>
        </Flex>
      )}
      {toggleSidebar && (
        <IconButton
          icon={isCollapsed ? <LuPanelLeftOpen size={18} /> : <LuPanelLeftClose size={18} />}
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          color="redesign.text.muted"
          _hover={{ bg: 'blackAlpha.50', color: 'redesign.heading', _dark: { bg: 'whiteAlpha.50' } }}
          aria-label={isCollapsed ? t('ariaLabels.expandSidebar') : t('ariaLabels.collapseSidebar')}
        />
      )}
    </Flex>
  );
};

// Sidebar content (shared between desktop and mobile)
const SidebarContent = ({ onClose, isCollapsed, toggleSidebar, onOpenSettings, isSettingsOpen, onOpenSearch }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation('navigation');
  const { t: tChat } = useTranslation('chat');
  const { can } = useWorkspace();
  const navItems = useNavItems();
  // What is waiting on each page: movements to review, and accounts whose
  // statements are behind or outdated.
  const { fixable } = useReviewCount();
  const freshness = useAccountFreshness(can('source_file.read'));
  const staleStatements = [...freshness.values()].filter((a) => a.status === 'behind' || a.status === 'outdated').length;
  const counts = {
    '/movements': { count: fixable, label: t('sidebar.toReview', { count: fixable }), color: 'redesign.accent.blue' },
    '/source-files': { count: staleStatements, label: t('sidebar.staleStatements', { count: staleStatements }), color: 'redesign.accent.alert' },
  };
  const openAssistant = () => {
    window.dispatchEvent(new Event(OPEN_ASSISTANT_EVENT));
    onClose?.();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose?.();
  };

  const handleSettingsClick = () => {
    onOpenSettings?.();
    onClose?.();
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  // "More" is lit while one of its pages is open, carries what is waiting
  // behind it, and takes its pages' tour stop.
  const moreItems = navItems.filter((item) => item.more);
  const moreCount = moreItems.map((item) => counts[item.to]).find((c) => c?.count > 0);

  return (
    // Flush on the window's chrome: the content panel beside it is what is
    // raised, so the sidebar needs no edge of its own.
    <Flex
      direction="column"
      h="100%"
      bg="redesign.bg.chrome"
    >
      {/* Logo */}
      <SidebarLogo isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />

      {/* Workspace switcher (MI-268) */}
      <WorkspaceSwitcher isCollapsed={isCollapsed} />

      {/* Search: opens the ⌘K menu. */}
      {onOpenSearch && (
        <Box px="8px" pb="10px">
          <Flex
            as="button"
            type="button"
            onClick={onOpenSearch}
            aria-label={t('sidebar.search')}
            title={isCollapsed ? t('sidebar.search') : undefined}
            w="100%"
            h="30px"
            align="center"
            justify={isCollapsed ? 'center' : 'flex-start'}
            gap="8px"
            px="9px"
            borderRadius="8px"
            bg="redesign.bg.sunken"
            border="1px solid"
            borderColor="redesign.border"
            color="redesign.text.muted"
            fontSize="13px"
            _hover={{ borderColor: 'redesign.border.strong', color: 'redesign.heading' }}
          >
            <LuSearch size={14} aria-hidden="true" />
            {!isCollapsed && <Text as="span" flex={1} textAlign="left" isTruncated>{t('sidebar.search')}</Text>}
            {!isCollapsed && <Kbd fontSize="2xs" bg="redesign.bg.raised" borderColor="redesign.border" color="redesign.text.muted">{MOD}K</Kbd>}
          </Flex>
        </Box>
      )}

      {/* Main Navigation, then the accounts; this part scrolls when the
          window is short. */}
      <Box flex={1} overflowY="auto" overflowX="hidden" minH={0} px="8px">
        <VStack spacing="2px" align="stretch">
          {navItems.filter((item) => !item.more).map((item) => (
            <NavItem
              key={item.to}
              icon={item.icon}
              label={item.label}
              to={item.to}
              dataTour={item.dataTour}
              isActive={isActive(item.to)}
              onClick={onClose}
              isCollapsed={isCollapsed}
              count={counts[item.to]?.count}
              countLabel={counts[item.to]?.label}
              countColor={counts[item.to]?.color}
            />
          ))}
          <Menu placement="right-start">
            <MenuButton
              as={NavItem}
              icon={<LuEllipsis />}
              label={t('sidebar.more')}
              dataTour={moreItems.find((item) => item.dataTour)?.dataTour}
              isActive={moreItems.some((item) => isActive(item.to))}
              isCollapsed={isCollapsed}
              count={moreCount?.count}
              countLabel={moreCount?.label}
              countColor={moreCount?.color}
            />
            <Portal>
              <MenuList fontSize="md" minW="200px" zIndex={2000}>
                {moreItems.map((item) => (
                  <MenuItem
                    key={item.to}
                    as={Link}
                    to={item.to}
                    onClick={onClose}
                    icon={<Box as="span" display="flex" fontSize="16px">{item.icon}</Box>}
                    fontWeight={isActive(item.to) ? 600 : 400}
                  >
                    <Flex align="center" gap="8px">
                      <Text flex={1}>{item.label}</Text>
                      {counts[item.to]?.count > 0 && (
                        <Text as="span" fontSize="sm" fontWeight={500} color={counts[item.to].color} textStyle="tabularNumbers" title={counts[item.to].label}>
                          {counts[item.to].count}
                        </Text>
                      )}
                    </Flex>
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </VStack>
        {!isCollapsed && <SidebarAccounts onClose={onClose} />}
      </Box>

      {/* Bottom Section */}
      <VStack spacing="2px" align="stretch" px="8px" pt="12px" pb="12px" borderTop="1px solid" borderColor="redesign.border">
        {/* MI-299: no "Chat" item — the floating assistant is the entry point
            to chat, and its expand button still opens the full /chat page.
            "Ask" opens that assistant, as ⌘J does. */}
        {location.pathname !== '/chat' && (
          <NavItem
            icon={<LuSparkles />}
            label={tChat('assistant.launcher')}
            onClick={openAssistant}
            isCollapsed={isCollapsed}
            trailing={onOpenSearch ? <Kbd fontSize="2xs" bg="redesign.bg.raised" borderColor="redesign.border" color="redesign.text.muted">{MOD}J</Kbd> : null}
          />
        )}
        <NavItem
          icon={<LuSettings />}
          label={t('settings')}
          isActive={isSettingsOpen}
          onClick={handleSettingsClick}
          isCollapsed={isCollapsed}
        />
        <NavItem
          icon={<LuLogOut />}
          label={t('logout')}
          onClick={handleLogout}
          isCollapsed={isCollapsed}
        />
      </VStack>
    </Flex>
  );
};

// Desktop Sidebar. It owns the shortcuts: ⌘K or ⌘F opens the command menu, as
// in Linear (⌘F in place of the browser's find), and ⌘J the assistant. A page
// with a filter bar (Dashboard, Movements) takes ⌘F first for its filter menu.
const DesktopSidebar = ({ isCollapsed, toggleSidebar, onOpenSettings, isSettingsOpen }) => {
  const search = useDisclosure();
  const pages = useNavItems();
  const { onOpen: openSearch } = search;

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.defaultPrevented || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      const key = e.key.toLowerCase();
      if (key === 'k' || key === 'f') {
        e.preventDefault();
        openSearch();
      } else if (key === 'j') {
        e.preventDefault();
        window.dispatchEvent(new Event(OPEN_ASSISTANT_EVENT));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openSearch]);

  return (
    <Box
      display={{ base: 'none', md: 'block' }}
      w={isCollapsed ? '60px' : '220px'}
      h="100vh"
      position="fixed"
      left={0}
      top={0}
      zIndex={100}
      transition="width 0.3s"
      overflow="hidden"
    >
      <SidebarContent
        isCollapsed={isCollapsed}
        toggleSidebar={toggleSidebar}
        onOpenSettings={onOpenSettings}
        isSettingsOpen={isSettingsOpen}
        onOpenSearch={openSearch}
      />
      <CommandMenu isOpen={search.isOpen} onClose={search.onClose} pages={pages} />
    </Box>
  );
};

// Mobile Sidebar (Drawer)
const MobileSidebar = ({ onOpenSettings, isSettingsOpen }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation('navigation');

  return (
    <>
      {/* Mobile Header Bar */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        position="fixed"
        top={0}
        left={0}
        right={0}
        h="44px"
        bg="redesign.bg.surface"
        borderBottom="1px solid"
        borderColor="redesign.border"
        align="center"
        px={2}
        zIndex={100}
      >
        <IconButton
          icon={<LuPanelLeftOpen size={16} />}
          variant="ghost"
          size="sm"
          onClick={onOpen}
          aria-label={t('ariaLabels.openMenu')}
        />
      </Flex>

      {/* Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent maxW="180px">
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <SidebarContent
              onClose={onClose}
              onOpenSettings={onOpenSettings}
              isSettingsOpen={isSettingsOpen}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

// Main Sidebar component
const Sidebar = ({ isCollapsed, toggleSidebar, onOpenSettings, isSettingsOpen }) => {
  return (
    <>
      <DesktopSidebar
        isCollapsed={isCollapsed}
        toggleSidebar={toggleSidebar}
        onOpenSettings={onOpenSettings}
        isSettingsOpen={isSettingsOpen}
      />
      <MobileSidebar
        onOpenSettings={onOpenSettings}
        isSettingsOpen={isSettingsOpen}
      />
    </>
  );
};

export default Sidebar;
